import React, { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import Chart from '../../../components/chart/BaseChart';
import { ChartSpec } from '../../../components/chart/chartSpec';
import { DepthExtremes } from './useSyncedDepthExtremes';

export interface LogTrackProps {
  /** Stable machine-readable id, e.g. 'rqi'. Also keys the spec tests. */
  trackId: string;
  title: string;
  series: Highcharts.SeriesOptionsType[];
  depthExtremes: DepthExtremes;
  onAfterSetExtremes: (event: {
    min?: number;
    max?: number;
    trigger?: string;
  }) => void;
  /** Value-axis configuration (horizontal axis of the inverted chart). */
  valueAxis?: {
    min?: number;
    max?: number;
    type?: 'linear' | 'logarithmic';
    reversed?: boolean;
  };
  /** Plot bands on the depth axis (Rock Type track). */
  depthPlotBands?: Highcharts.XAxisPlotBandsOptions[];
  /** Only the leftmost track renders depth labels. */
  showDepthLabels?: boolean;
  /** Off for the depth track, whose plot strip carries no curve. */
  showValueLabels?: boolean;
  height: number;
  /** Enables Highcharts boost on dense line tracks. */
  boost?: boolean;
  /** Lets the viewer drive this chart's depth window imperatively. */
  chartRef?: React.Ref<HighchartsReact.RefObject>;
}

// Every track uses the same top and bottom margins, so all plot areas are
// exactly the same height and a given depth lands on the same pixel row
// across the whole viewer. A per-track margin -- reserving space for a
// legend, say -- silently skews the depth alignment, which is why the
// legend is rendered as HTML beside the tracks instead.
/*
 * Just enough headroom for the topmost depth tick label, which is centred on
 * the tick at the plot's top edge. The value-axis title and labels used to live
 * in here and needed 48px; they are now React pills in the header row above,
 * which returns 36px of plot height to every track and lets a track carry four
 * labelled curve identities instead of one axis title.
 */
export const MARGIN_TOP = 12;
export const MARGIN_BOTTOM = 12;
// Room for the depth labels plus their tick. No longer has to fit a rotated
// axis title too -- the depth track names itself at the top, like every other
// track -- but a five-figure depth with a thousands separator ("24 000") still
// needs most of this.
const MARGIN_LEFT_WITH_LABELS = 68;
const MARGIN_LEFT_PLAIN = 14;

/**
 * Suppress Highcharts' floating "Reset zoom" button. It overlays the plot
 * and only resets the chart it belongs to, leaving the other tracks
 * zoomed; the viewer's toolbar button resets them together. Returning
 * false from this hook prevents the button being created at all.
 *
 * `beforeShowResetZoom` is fired by Highcharts but is absent from the
 * shipped typings, hence the cast.
 */
const SUPPRESS_RESET_ZOOM_BUTTON = {
  beforeShowResetZoom: () => false
} as unknown as Highcharts.ChartEventsOptions;

/**
 * One vertical log track: an inverted Highcharts chart whose x axis is
 * depth (drawn vertically, increasing downwards) and whose y axis is the
 * curve value (drawn horizontally, labelled at the top, Petrel style).
 */
export const LogTrack: React.FC<LogTrackProps> = ({
  trackId,
  title,
  series,
  depthExtremes,
  onAfterSetExtremes,
  valueAxis,
  depthPlotBands,
  showDepthLabels = false,
  showValueLabels = true,
  height,
  boost = false,
  chartRef
}) => {
  const spec = useMemo<ChartSpec>(
    () => ({
      type: 'line',
      series,
      legend: false,
      exportEnabled: false,
      height,
      zoomType: 'x',
      margin: {
        left: showDepthLabels ? MARGIN_LEFT_WITH_LABELS : MARGIN_LEFT_PLAIN,
        right: 6,
        top: MARGIN_TOP,
        bottom: MARGIN_BOTTOM
      },
      xAxis: {
        min: depthExtremes.min,
        max: depthExtremes.max,
        // Off in favour of the viewer's shared crosshair. A per-chart one could
        // only ever mark the track the pointer was over, so hovering told you
        // about one curve instead of one depth across every track.
        crosshair: false,
        ...(depthPlotBands ? { plotBands: depthPlotBands } : {})
      },
      yAxis: {
        // No title and no labels: the header pill above states the curve name,
        // its unit and the scale, adjacent to the track it describes.
        title: undefined,
        ...(valueAxis?.min !== undefined ? { min: valueAxis.min } : {}),
        ...(valueAxis?.max !== undefined ? { max: valueAxis.max } : {}),
        ...(valueAxis?.type ? { type: valueAxis.type } : {}),
        ...(valueAxis?.reversed !== undefined
          ? { reversed: valueAxis.reversed }
          : {})
      },
      ...(boost ? { boost: { threshold: 2000 } } : {}),
      plotOptions: {
        series: {
          marker: { enabled: false },
          lineWidth: 1,
          animation: false,
          states: { hover: { lineWidthPlus: 0 } },
          turboThreshold: 0
        }
      },
      options: {
        chart: {
          inverted: true,
          events: SUPPRESS_RESET_ZOOM_BUTTON
        },
        xAxis: {
          labels: { enabled: showDepthLabels },
          events: { afterSetExtremes: onAfterSetExtremes as any },
          gridLineWidth: 1,
          /*
           * Load-bearing for the shared crosshair, not cosmetic. Highcharts
           * otherwise rounds the depth domain outward to the nearest tick, so
           * `axis.min` stops equalling the requested window -- and the
           * crosshair projects depth to pixels from that window arithmetically
           * rather than by measuring the chart. Round the domain and the line
           * drifts from the curves by up to one tick, silently.
           */
          startOnTick: false,
          endOnTick: false,
          title: {
            margin: 6,
            style: { fontSize: '10px', fontWeight: 'bold' }
          }
        },
        yAxis: {
          opposite: true,
          /*
           * Same reason, different axis: a track scale of 0-1 that Highcharts
           * widened out to a round tick would no longer match the min/max the
           * header states.
           */
          startOnTick: false,
          endOnTick: false,
          gridLineWidth: showValueLabels ? 1 : 0,
          labels: { enabled: false },
          title: { text: undefined }
        },
        // Same reason: the shared readout answers for every track at once.
        tooltip: { enabled: false }
      }
    }),
    [
      series,
      height,
      depthExtremes,
      depthPlotBands,
      showDepthLabels,
      showValueLabels,
      title,
      valueAxis,
      boost,
      onAfterSetExtremes
    ]
  );

  return (
    <Chart
      spec={spec}
      chartRef={chartRef}
      // The spec tests key captured charts off this rather than off the axis
      // title, which is a human label that a copy edit can silently break --
      // and which is about to stop existing.
      containerProps={{ 'data-testid': `log-track-chart-${trackId}` }}
    />
  );
};

export default LogTrack;
